import { Card } from '@/components/ui/Card';
import { AlertTriangle, Droplets, UtensilsCrossed, Info } from 'lucide-react';
import { cn } from '@/lib/cn';
import type { AdviceItem } from '@/lib/advice';

interface WaterAdvicePanelProps {
  advice: AdviceItem[];
}

const CATEGORY_ICONS = {
  water: Droplets,
  feeding: UtensilsCrossed,
  general: Info,
};

const SEVERITY_STYLES = {
  critical: {
    bg: 'bg-red-50',
    border: 'border-red-200',
    icon: 'text-red-500',
    title: 'text-red-800',
    text: 'text-red-700',
  },
  warning: {
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    icon: 'text-amber-500',
    title: 'text-amber-800',
    text: 'text-amber-700',
  },
  info: {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    icon: 'text-blue-500',
    title: 'text-blue-800',
    text: 'text-blue-700',
  },
};

export function WaterAdvicePanel({ advice }: WaterAdvicePanelProps) {
  if (advice.length === 0) return null;

  // Sort: critical first, then warning, then info
  const sorted = [...advice].sort((a, b) => {
    const order = { critical: 0, warning: 1, info: 2 };
    return order[a.severity] - order[b.severity];
  });

  return (
    <Card>
      <Card.Header>
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-amber-100 rounded-lg">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
          </div>
          Advice
          <span className="text-xs text-gray-400 font-normal">({advice.length})</span>
        </div>
      </Card.Header>
      <Card.Body className="space-y-2">
        {sorted.map((item) => {
          const styles = SEVERITY_STYLES[item.severity];
          const Icon = CATEGORY_ICONS[item.category];

          return (
            <div
              key={item.id}
              className={cn(
                'rounded-lg border p-3',
                styles.bg,
                styles.border,
              )}
            >
              <div className="flex items-start gap-2">
                <Icon className={cn('w-4 h-4 mt-0.5 shrink-0', styles.icon)} />
                <div className="min-w-0">
                  <p className={cn('text-sm font-medium', styles.title)}>
                    {item.title}
                  </p>
                  <p className={cn('text-xs mt-0.5', styles.text)}>
                    {item.action}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </Card.Body>
    </Card>
  );
}
